import {
    CopilotModel,
    defaultSelectedContexts,
    IChatMessage,
    IChatMessageData,
    IChatMessageReaction,
    IChatMessageThread,
    isHumanChatMessage,
} from "@buildwithflux/models";
import {ChatRepository, DocumentRepository} from "@buildwithflux/repositories";
import {Logger} from "@buildwithflux/shared";
import {findLast, keys, omitBy, values} from "lodash";
import {uuid} from "short-uuid";

import {CurrentUserService} from "modules/auth";
import {getDefaultModel} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/helpers/getDefaultModel";
import {determineNextUntitledThreadName} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/helpers/determineNextUntitledThreadName";
import {threadNameFromMessageText} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/helpers/threadNameHelpers";
import {createSortedThreadMessagesSelector} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/selectors/createSortedThreadMessagesSelector";
import {selectUntitledThreads} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/selectors/selectUntitledThreads";
import {
    CopilotChatStoreGetter,
    CopilotChatStoreSetter,
} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/useCopilotChatStore";

export function setCurrentThreadToLatest(_set: CopilotChatStoreSetter, get: CopilotChatStoreGetter) {
    return () => {
        const firstThreadUid = values(get().threads).sort((a, b) => b.updated_at - a.updated_at)[0]?.uid;

        if (firstThreadUid) {
            get().setCurrentThread(firstThreadUid);
        } else {
            get().setCurrentThread(undefined);
        }
    };
}

function addAddedOrUpdatedMessages(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    threadUid: string,
    addedOrChangedMessages: IChatMessageData[],
) {
    const updatedMessages = addedOrChangedMessages.reduce(
        (
            acc: {
                [messageUid: string]: IChatMessage;
            },
            messageData,
        ) => {
            acc[messageData.uid] = {
                ...messageData,
                reactions: {},
            };

            return acc;
        },
        {},
    );

    set((state) => {
        const previousThread = state.threads[threadUid];

        if (!previousThread) return;

        const newThread = {
            ...previousThread,
            messages: {
                ...previousThread.messages,
                ...updatedMessages,
            },
        };

        return {
            ...state,
            threads: {
                ...state.threads,
                [threadUid]: newThread,
            },
        };
    });

    get().setCurrentThreadLoaded(true);
}

function subscribeToMessageReactions(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    currentDocumentUid: string,
    threadUid: string,
    addedOrChangedMessages: IChatMessageData[],
    chatRepository: ChatRepository,
    logger: Logger,
) {
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    values(addedOrChangedMessages).forEach((message) => {
        const messageReactionsSubscription = get().threadMessagesReactionsSubscription[message.uid];

        if (messageReactionsSubscription) {
            messageReactionsSubscription();
        }

        const subscription = chatRepository.subscribeToMessageReactions(
            currentDocumentUid,
            threadUid,
            message.uid,
            (addedOrChangedReactions) => {
                // On Receive
                set((state) => {
                    const thread = state.threads[threadUid];
                    const messageState = thread?.messages[message.uid];

                    if (!thread || !messageState) return state;

                    return {
                        ...state,
                        threads: {
                            ...state.threads,
                            [threadUid]: {
                                ...thread,
                                messages: {
                                    ...thread.messages,
                                    [message.uid]: {
                                        ...messageState,
                                        reactions: {
                                            ...messageState.reactions,
                                            ...addedOrChangedReactions.reduce(
                                                (
                                                    acc: {
                                                        [reactionUid: string]: IChatMessageReaction;
                                                    },
                                                    reactionData,
                                                ) => {
                                                    acc[reactionData.uid] = reactionData;

                                                    return acc;
                                                },
                                                {},
                                            ),
                                        },
                                    },
                                },
                            },
                        },
                    };
                });
            },
            (removedReactions) => {
                // On Delete
                set((state) => {
                    const uidsToRemove = keys(removedReactions);
                    const thread = state.threads[threadUid];
                    const messageState = thread?.messages[message.uid];

                    if (!thread || !messageState) return state;

                    return {
                        ...state,
                        threads: {
                            ...state.threads,
                            [threadUid]: {
                                ...thread,
                                messages: {
                                    ...thread.messages,
                                    [message.uid]: {
                                        ...messageState,
                                        reactions: omitBy(messageState.reactions, (value) =>
                                            uidsToRemove.includes(value.uid),
                                        ),
                                    },
                                },
                            },
                        },
                    };
                });
            },
            (error) => {
                logger.error("Error subscribing to chat message reactions", error);
            },
        );

        set((state) => {
            return {
                ...state,
                threadMessagesReactionsSubscription: {
                    ...state.threadMessagesReactionsSubscription,
                    [message.uid]: subscription,
                },
            };
        });
    });
}

function onAddedOrUpdatedMessages(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    threadUid: string,
    chatRepository: ChatRepository,
    currentDocumentUid: string,
    logger: Logger,
) {
    return (addedOrChangedMessages: IChatMessageData[]) => {
        // On Receive
        addAddedOrUpdatedMessages(set, get, threadUid, addedOrChangedMessages);

        subscribeToMessageReactions(
            set,
            get,
            currentDocumentUid,
            threadUid,
            addedOrChangedMessages,
            chatRepository,
            logger,
        );
    };
}

function onRemovedMessages(set: CopilotChatStoreSetter, get: CopilotChatStoreGetter, threadUid: string) {
    return (removedMessages: IChatMessageData[]) => {
        // On Delete
        set((state) => {
            const uidsToRemove = keys(removedMessages);

            const thread = state.threads[threadUid];

            if (!thread) return state;

            const remainingMessages = omitBy(thread.messages, (value) => uidsToRemove.includes(value.uid));

            return {
                ...state,
                threads: {
                    ...state.threads,
                    [threadUid]: {
                        ...thread,
                        messages: remainingMessages,
                    },
                },
            };
        });

        get().setCurrentThreadLoaded(true);

        set((state) => {
            const thread = state.threads[threadUid];

            const shouldShowWelcomeMessage =
                thread && thread.uid === state.currentThreadUid && values(thread.messages).length === 0;

            if (shouldShowWelcomeMessage) {
                state.isShowingWelcomeMessage = true;
            }
        });

        // remove reaction subscriptions
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        values(removedMessages).forEach((message) => {
            const messageReactionsSubscription = get().threadMessagesReactionsSubscription[message.uid];

            if (messageReactionsSubscription) {
                messageReactionsSubscription();
            }
        });
    };
}

export function setCurrentThread(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    chatRepository: ChatRepository,
    logger: Logger,
) {
    return (threadUid?: string) => {
        const {currentDocumentUid} = get();
        if (!currentDocumentUid) return;

        const previousThreadUid = get().currentThreadUid;
        const switchingThreads = threadUid && threadUid !== previousThreadUid;

        set((draft) => {
            draft.currentThreadUid = threadUid;
        });

        get().setCurrentThreadLoaded(false);

        // TODO: clean this up. hard to read
        set(
            (draft) => {
                if (switchingThreads) {
                    const previousInput = draft.draftMessages[threadUid]?.text;
                    draft.newMessageInput.text = previousInput ?? "";

                    let lastModel: CopilotModel | undefined = undefined;
                    const sortedMessages = createSortedThreadMessagesSelector(threadUid)(draft);
                    // TODO: fix type casts here... something wrong
                    const lastUserMessage = findLast(sortedMessages, isHumanChatMessage);
                    lastModel = lastUserMessage?.meta_data.selected_model;
                    draft.newMessageInput.model = lastModel ?? getDefaultModel(get().isFluxOrganizationMember);
                    draft.canCreateThread = true;
                }
                const thread = threadUid ? draft.threads[threadUid] : undefined;
                draft.navbarTitle = thread?.name;

                if (!threadUid) return;

                if (draft.threadMessagesSubscription) {
                    draft.threadMessagesSubscription();
                }

                draft.threadMessagesSubscription = chatRepository.subscribeToThreadMessages(
                    currentDocumentUid,
                    threadUid,
                    onAddedOrUpdatedMessages(set, get, threadUid, chatRepository, currentDocumentUid, logger),
                    onRemovedMessages(set, get, threadUid),
                    (error) => {
                        logger.error("Error subscribing to chat messages", error);
                    },
                );
            },
            undefined,
            "big set in setCurrentThread",
        );

        if (switchingThreads) {
            get().setNewMessageInput({focused: true});
        }
    };
}

export function createThread(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    currentUserService: CurrentUserService,
    chatRepository: ChatRepository,
    _documentRepository: DocumentRepository,
    logger: Logger,
) {
    return async (
        uid?: string,
        initialMessageText?: string,
        planningDisabled?: boolean,
    ): Promise<string | undefined> => {
        const {currentDocumentUid, currentThreadUid: originalCurrentThreadUid} = get();

        if (!currentDocumentUid) return;

        const currentUser = currentUserService.getCurrentUser();
        if (!currentUser) return;

        const threadUid = uid ?? uuid();
        const timestamp = Date.now();

        let initialThreadName: string;
        if (initialMessageText) {
            initialThreadName = threadNameFromMessageText(initialMessageText);
        } else {
            const untitledThreads = selectUntitledThreads(get());
            const untitledThreadNames = untitledThreads
                .map((t) => t.name)
                .filter((name: string | undefined): name is string => Boolean(name));

            initialThreadName = determineNextUntitledThreadName(untitledThreadNames);
        }

        // Create thread immediately with planningDisabled as undefined initially
        // We'll update it after fetching the document
        const newThread: IChatMessageThread = {
            uid: threadUid,
            owner_uid: currentUser.uid,
            owner_handle: currentUser.handle,
            messages: {},
            created_at: timestamp,
            updated_at: timestamp,
            name: initialThreadName,
            namedBy: "initializer" as const, // IMPORTANT: tells backend we can rename it with copilot
        };

        if (planningDisabled !== undefined) {
            newThread.planningDisabled = planningDisabled;
        }

        // Optimistically update the state FIRST so the thread exists immediately
        set((draft) => {
            draft.threads[threadUid] = newThread;
        });

        try {
            // Persist the new thread to the backend BEFORE setting up the subscription.
            // This is critical because setCurrentThread() sets up a Firestore subscription
            // to the thread's messages. If we set up the subscription before the thread
            // exists in Firestore, the subscription may be in a bad state and won't
            // receive message updates correctly.
            await chatRepository.createThread(currentDocumentUid, newThread);

            // Now that the thread exists in Firestore, set up the subscription
            get().setCurrentThread(threadUid);

            return threadUid;
        } catch (error) {
            logger.error("Failed to create thread:", error);

            // Rollback optimistic update in case of an error
            set((draft) => {
                delete draft.threads[threadUid]; // Remove the thread
                if (draft.currentThreadUid === threadUid) {
                    draft.currentThreadUid = originalCurrentThreadUid; // Reset current thread UID if it was the new one
                    draft.isShowingWelcomeMessage = false;
                }
            });
        }
    };
}

export function updateThreadName(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    chatRepository: ChatRepository,
    logger: Logger,
) {
    return async (threadUid: string, name: string) => {
        const {currentDocumentUid, threads} = get();

        const originalThread = threads[threadUid]; // Original thread for rollback

        // Early exit if prerequisites are not met
        if (!currentDocumentUid || !originalThread) return;

        // Optimistically update the UI
        set((draft) => {
            const draftThread = draft.threads[threadUid];
            if (draftThread) {
                draftThread.name = name;
                draftThread.updated_at = Date.now();
                draftThread.namedBy = "user";
            }
        });

        try {
            // Use updateThread to only update specific fields without overwriting
            // backend-written fields like latest_brief.executionId and latest_brief.executionUpdatedAt
            await chatRepository.updateThread(currentDocumentUid, threadUid, {
                name,
                updated_at: Date.now(),
                namedBy: "user",
            });
        } catch (error) {
            logger.error("Failed to update thread name:", error);

            // Rollback the optimistic update on failure
            set((draft) => {
                const draftThread = draft.threads[threadUid];
                if (draftThread) {
                    draftThread.name = originalThread.name; // Revert to the original name
                    draftThread.updated_at = originalThread.updated_at; // Revert to the original timestamp
                    draftThread.namedBy = originalThread.namedBy; // Revert to the original namedBy
                }
            });
        }
    };
}

export function deleteThread(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    chatRepository: ChatRepository,
    logger: Logger,
) {
    return async (threadUid: string) => {
        const {currentDocumentUid, threads} = get();

        // Store a backup of the thread for rollback
        const deletedThread = threads[threadUid];

        // Early exit if prerequisites are not met
        if (!currentDocumentUid || !deletedThread) return;

        // Optimistically update the state
        set((draft) => {
            delete draft.threads[threadUid];

            if (keys(draft.threads).length === 0) {
                // We're back at the initial state
                draft.isShowingWelcomeMessage = true;
                draft.canCreateThread = false;
                draft.setNewMessageInput({
                    text: "",
                    model: getDefaultModel(get().isFluxOrganizationMember),
                    selectedContexts: defaultSelectedContexts,
                    focused: true,
                });
            }
        });

        get().setCurrentThreadToLatest();

        try {
            // Persist the deletion to the backend
            await chatRepository.deleteThread(currentDocumentUid, threadUid);
        } catch (error) {
            logger.error("Failed to delete thread:", error);

            // Rollback optimistic update on failure
            set((draft) => {
                if (deletedThread) {
                    draft.threads[threadUid] = deletedThread;
                    draft.isShowingWelcomeMessage = true;

                    // Optionally, reset the current thread UID if the deleted thread was the current one
                    if (!draft.currentThreadUid) {
                        draft.currentThreadUid = threadUid;
                        draft.isShowingWelcomeMessage = false;
                    }
                }
            });
        }
    };
}
